import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

const BASE_TITLE = 'Property Estate Rwanda'

const routes: Array<RouteRecordRaw> = [
  // ── Landing page (public, redirects to dashboard if logged in) ──────────
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/views/LandingPage.vue'),
    meta: {
      requiresAuth: false,
      title: 'Property Estate Rwanda — Smart Property Management for Rwanda',
      description: 'The all-in-one property management platform for landlords and agencies across Rwanda. Manage properties, tenants, invoices, and payments in one place.'
    }
  },

  // ── Public ────────────────────────────────────────────────────────────────
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: {
      requiresAuth: false,
      title: `Sign In | ${BASE_TITLE}`,
      description: 'Sign in to your Property Estate Rwanda account to manage your properties, tenants and rental income across Rwanda.'
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: {
      requiresAuth: false,
      title: `Create Account | ${BASE_TITLE}`,
      description: 'Register a free account on Property Estate Rwanda and start managing your properties, units and tenants today.'
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: `Forgot Password | ${BASE_TITLE}`,
      description: 'Reset your Property Estate Rwanda account password securely.'
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: `Reset Password | ${BASE_TITLE}`,
      description: 'Set a new password for your Property Estate Rwanda account.'
    }
  },

  // ── Protected (inside AppLayout) ─────────────────────────────────────────
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: {
          title: `Dashboard | ${BASE_TITLE}`,
          description: 'Overview of your properties, rental income, occupancy rates and pending tasks on Property Estate Rwanda.'
        }
      },
      {
        path: 'properties',
        name: 'Properties',
        component: () => import('@/views/properties/PropertiesView.vue'),
        meta: {
          title: `Properties | ${BASE_TITLE}`,
          description: 'Manage all your rental properties across Rwanda — view occupancy, income, and property details.'
        }
      },
      {
        path: 'properties/:id',
        name: 'PropertyDetail',
        component: () => import('@/views/properties/PropertyDetailView.vue'),
        meta: {
          title: `Property Details | ${BASE_TITLE}`,
          description: 'View detailed information about a property including units, tenants, expenses and maintenance requests.'
        }
      },
      {
        path: 'properties/:id/history',
        name: 'PropertyHistory',
        component: () => import('@/views/properties/PropertyHistoryView.vue'),
        meta: {
          title: `Property History | ${BASE_TITLE}`,
          description: 'Full occupancy and revenue history for a property — export to PDF or CSV.'
        }
      },
      {
        path: 'units',
        name: 'Units',
        component: () => import('@/views/units/UnitsView.vue'),
        meta: {
          title: `Units | ${BASE_TITLE}`,
          description: 'Track all rental units — occupied and vacant — across your properties in Rwanda.'
        }
      },
      {
        path: 'tenants',
        name: 'Tenants',
        component: () => import('@/views/tenants/TenantsView.vue'),
        meta: {
          title: `Tenants | ${BASE_TITLE}`,
          description: 'Manage your tenants, lease agreements, and contact information for all rental properties in Rwanda.'
        }
      },
      {
        path: 'tenants/:id',
        name: 'TenantDetail',
        component: () => import('@/views/tenants/TenantDetailView.vue'),
        meta: {
          title: `Tenant Details | ${BASE_TITLE}`,
          description: 'View a tenant lease details, invoice history and payment records on Property Estate Rwanda.'
        }
      },
      {
        path: 'tenants/:id/history',
        name: 'TenantHistory',
        component: () => import('@/views/tenants/TenantHistoryView.vue'),
        meta: {
          title: `Tenant History | ${BASE_TITLE}`,
          description: 'Full lease and payment history for a tenant — export to PDF or CSV.'
        }
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/views/invoices/InvoicesView.vue'),
        meta: {
          title: `Invoices | ${BASE_TITLE}`,
          description: 'Generate and track rent invoices, monitor overdue payments and manage billing for your Rwanda properties.'
        }
      },
      {
        path: 'payments',
        name: 'Payments',
        component: () => import('@/views/payments/PaymentsView.vue'),
        meta: {
          title: `Payment History | ${BASE_TITLE}`,
          description: 'View all rent payment records, filter by property or month, and track collection totals for your Rwanda rentals.'
        }
      },
      {
        path: 'expenses',
        name: 'Expenses',
        component: () => import('@/views/expenses/ExpensesView.vue'),
        meta: {
          title: `Expenses | ${BASE_TITLE}`,
          description: 'Record and track property expenses, maintenance costs and operating expenditures across your Rwanda portfolio.'
        }
      },
      {
        path: 'maintenance',
        name: 'Maintenance',
        component: () => import('@/views/maintenance/MaintenanceView.vue'),
        meta: {
          title: `Maintenance Requests | ${BASE_TITLE}`,
          description: 'Manage and resolve maintenance and repair requests submitted by tenants for your Rwanda properties.'
        }
      },
      {
        path: 'users',
        name: 'Users',
        component: () => import('@/views/users/UsersView.vue'),
        meta: {
          requiresAdmin: true,
          title: `Team Members | ${BASE_TITLE}`,
          description: 'Manage team members and staff accounts for your Property Estate Rwanda organisation.'
        }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: {
          requiresAdmin: true,
          title: `Settings | ${BASE_TITLE}`,
          description: 'Configure your Property Estate Rwanda account, organisation profile and system preferences.'
        }
      },
      {
        path: 'admin/accounts',
        name: 'AdminAccounts',
        component: () => import('@/views/admin/AdminAccountsView.vue'),
        meta: {
          requiresAdmin: true,
          title: `All Accounts | Admin | ${BASE_TITLE}`,
          description: 'Monitor all registered landlord and agency accounts on the platform.'
        }
      },
      {
        path: 'admin/accounts/:id',
        name: 'AdminAccountDetail',
        component: () => import('@/views/admin/AdminAccountDetailView.vue'),
        meta: {
          requiresAdmin: true,
          title: `Account Details | Admin | ${BASE_TITLE}`,
          description: 'View detailed activity, properties, invoices and payments for a registered account.'
        }
      }
    ]
  },

  // ── Fallback ──────────────────────────────────────────────────────────────
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// ── Navigation guard ─────────────────────────────────────────────────────────
router.beforeEach((to, _, next) => {
  const token = localStorage.getItem('access_token')
  const isAuthenticated = !!token

  if (to.meta.requiresAuth === false) {
    if (isAuthenticated && ['Landing', 'Login', 'Register'].includes(to.name as string)) {
      return next('/dashboard')
    }
    return next()
  }

  if (!isAuthenticated) {
    return next('/')
  }

  // Enforce admin-only routes
  if (to.meta.requiresAdmin) {
    const user = JSON.parse(localStorage.getItem('user') || 'null')
    if (!user || user.role !== 'admin') {
      return next('/dashboard')
    }
  }

  next()
})

// ── SEO: update title & meta description on every route change ───────────────
router.afterEach((to) => {
  const title = (to.meta.title as string) ?? 'Property Estate Rwanda — Gateway to Real Estate'
  const description = (to.meta.description as string) ?? 'Smart property management for landlords and agencies across Rwanda.'

  document.title = title

  let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]')
  if (!metaDesc) {
    metaDesc = document.createElement('meta')
    metaDesc.setAttribute('name', 'description')
    document.head.appendChild(metaDesc)
  }
  metaDesc.setAttribute('content', description)

  const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]')
  if (ogTitle) ogTitle.setAttribute('content', title)

  const ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]')
  if (ogDesc) ogDesc.setAttribute('content', description)
})

export default router
